import type { MyBrandFilterTuple } from "@nexus/contracts";
import { formatBrandName } from "./format";

export interface FilterOption {
  id: string;
  name: string;
}

const PLATFORM_DISPLAY_NAMES: Record<string, string> = {
  meta: "Meta",
  google: "Google",
  tiktok: "TikTok",
};

export function platformDisplayName(slug: string): string {
  return PLATFORM_DISPLAY_NAMES[slug] ?? formatBrandName(slug);
}

export function prunePlatforms(selected: string[], allowed: string[]): string[] {
  const canonicalById = new Map(allowed.map((id) => [id.toLowerCase(), id]));
  const kept = new Set<string>();
  for (const id of selected) {
    const canonical = canonicalById.get(id.toLowerCase());
    if (canonical) kept.add(canonical);
  }
  return [...kept];
}

function filterTuplesByPlatform(
  tuples: MyBrandFilterTuple[],
  selectedPlatforms: string[] | null,
): MyBrandFilterTuple[] {
  if (selectedPlatforms === null) return tuples;
  if (selectedPlatforms.length === 0) return [];
  const allowed = new Set(selectedPlatforms);
  return tuples.filter((t) => allowed.has(t.platform));
}

function filterTuplesByAccounts(
  tuples: MyBrandFilterTuple[],
  selectedAccounts: string[] | null,
): MyBrandFilterTuple[] {
  if (selectedAccounts === null) return tuples;
  if (selectedAccounts.length === 0) return [];
  const allowed = new Set(selectedAccounts);
  return tuples.filter((t) => allowed.has(t.accountName));
}

export function derivePlatforms(tuples: MyBrandFilterTuple[]): FilterOption[] {
  const seen = new Set<string>();
  const options: FilterOption[] = [];
  for (const t of tuples) {
    if (seen.has(t.platform)) continue;
    seen.add(t.platform);
    options.push({ id: t.platform, name: platformDisplayName(t.platform) });
  }
  return options.sort((a, b) => a.name.localeCompare(b.name));
}

export function deriveAdAccounts(
  tuples: MyBrandFilterTuple[],
  selectedPlatforms: string[] | null,
): FilterOption[] {
  const scoped = filterTuplesByPlatform(tuples, selectedPlatforms);
  const seen = new Set<string>();
  const options: FilterOption[] = [];
  for (const t of scoped) {
    if (seen.has(t.accountName)) continue;
    seen.add(t.accountName);
    options.push({ id: t.accountName, name: t.accountName });
  }
  return options.sort((a, b) => a.name.localeCompare(b.name));
}

export function deriveProducts(
  tuples: MyBrandFilterTuple[],
  selectedPlatforms: string[] | null,
  selectedAccounts: string[] | null,
): FilterOption[] {
  let scoped = filterTuplesByPlatform(tuples, selectedPlatforms);
  scoped = filterTuplesByAccounts(scoped, selectedAccounts);
  const seen = new Set<string>();
  const options: FilterOption[] = [];
  for (const t of scoped) {
    if (seen.has(t.productCode)) continue;
    seen.add(t.productCode);
    options.push({ id: t.productCode, name: formatBrandName(t.productCode) });
  }
  return options.sort((a, b) => a.name.localeCompare(b.name));
}

export function pruneMyBrandSelections(
  tuples: MyBrandFilterTuple[],
  filters: {
    platforms: string[] | null;
    accounts: string[] | null;
    products: string[] | null;
  },
  changedKey: "platforms" | "accounts" | "products",
): { platforms: string[] | null; accounts: string[] | null; products: string[] | null } {
  const platforms = filters.platforms;
  let accounts = filters.accounts;
  let products = filters.products;

  if (changedKey === "platforms") {
    if (accounts !== null) {
      const allowedAccounts = new Set(
        deriveAdAccounts(tuples, platforms).map((a) => a.id),
      );
      const pruned = accounts.filter((id) => allowedAccounts.has(id));
      accounts = pruned.length > 0 ? pruned : null;
    }
    const allowedProducts = new Set(
      deriveProducts(tuples, platforms, accounts).map((p) => p.id),
    );
    if (products !== null) {
      const pruned = products.filter((id) => allowedProducts.has(id));
      products = pruned.length > 0 ? pruned : null;
    }
    return { platforms, accounts, products };
  }

  if (changedKey === "accounts") {
    const allowedProducts = new Set(
      deriveProducts(tuples, platforms, accounts).map((p) => p.id),
    );
    if (products !== null) {
      const pruned = products.filter((id) => allowedProducts.has(id));
      products = pruned.length > 0 ? pruned : null;
    }
    return { platforms, accounts, products };
  }

  return { platforms, accounts, products };
}

export function allAdAccountIds(
  tuples: MyBrandFilterTuple[],
  selectedPlatforms: string[] | null,
): string[] {
  return deriveAdAccounts(tuples, selectedPlatforms).map((a) => a.id);
}

export function allProductCodes(
  tuples: MyBrandFilterTuple[],
  selectedPlatforms: string[] | null,
  selectedAccounts: string[] | null,
): string[] {
  return deriveProducts(tuples, selectedPlatforms, selectedAccounts).map((p) => p.id);
}
